import { ActionIcon, Modal, Tabs } from "@mantine/core";
import { X } from "lucide-react";
import { useState } from "react";

import type { PickedImage } from "../../../platform/media/ImagePicker";
import type { ListedProduct } from "../../../platform/media/ProductListPopover";
import classes from "./ad-copies.module.css";
import { GenerateCopy } from "./GenerateCopy";
import type { Picks } from "./picks";
import { SavedCopies, type Tile } from "./SavedCopies";
import { WriteCopy } from "./WriteCopy";

export interface AdCopiesModalProps {
  opened: boolean;
  onClose: () => void;
  teamId: string;
  brandId: string;
  /** The draft's product: Saved Copies lists its copies, Write starts with it. */
  product: ListedProduct;
  /** The draft's first reference: AI Generate's inspiration at first. */
  inspiration: PickedImage | null;
  tiles: readonly Tile[];
  confirmed: Picks;
  onConfirm: (picks: Picks) => void;
  onClear: () => void;
}

type Tab = "saved" | "generate" | "write";

/**
 * Live's Ad copies modal (`text-copies-modal`, captures advanced-234/12 and studio-depth 29 to 31):
 * wide, with no title, the tabs "Saved Copies", "AI Generate" and "Write", and an x. Each opening starts on Saved Copies, and a tab mounts afresh each time, as
 * live's: ticks not confirmed go with a tab change.
 */
export function AdCopiesModal(props: AdCopiesModalProps) {
  return (
    <Modal.Root opened={props.opened} onClose={props.onClose} centered size="100%">
      <Modal.Overlay />
      <Modal.Content aria-label="Ad copies">
        <Modal.Body className={classes.modalBody}>
          {/* Mounted while open: each opening starts on Saved Copies from the draft's ticks. */}
          <Content {...props} />
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

function Content({
  onClose,
  teamId,
  brandId,
  product,
  inspiration,
  tiles,
  confirmed,
  onConfirm,
  onClear,
}: AdCopiesModalProps) {
  const [tab, setTab] = useState<Tab>("saved");
  return (
    <div className={classes.shell}>
      <ActionIcon
        variant="subtle"
        color="gray"
        className={classes.close}
        aria-label="Close"
        onClick={onClose}
      >
        <X size={20} aria-hidden />
      </ActionIcon>
      <Tabs
        value={tab}
        onChange={(next) =>
          next === "saved" || next === "generate" || next === "write" ? setTab(next) : undefined
        }
        keepMounted={false}
        className={classes.tabs}
      >
        <Tabs.List>
          <Tabs.Tab value="saved">Saved Copies</Tabs.Tab>
          <Tabs.Tab value="generate">AI Generate</Tabs.Tab>
          <Tabs.Tab value="write">Write</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="saved" className={classes.panel}>
          <SavedCopies
            teamId={teamId}
            brandId={brandId}
            productId={product.productId}
            tiles={tiles}
            confirmed={confirmed}
            onConfirm={onConfirm}
            onClear={onClear}
          />
        </Tabs.Panel>
        <Tabs.Panel value="generate" className={classes.panel}>
          <GenerateCopy
            teamId={teamId}
            brandId={brandId}
            product={product}
            inspiration={inspiration}
            onStarted={() => setTab("saved")}
          />
        </Tabs.Panel>
        <Tabs.Panel value="write" className={classes.panel}>
          <WriteCopy
            teamId={teamId}
            brandId={brandId}
            product={product}
            onSaved={() => setTab("saved")}
          />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
